import type { Action, ScheduleActionDisplayOptions } from "@core/features/actions";
import { useSubSteps, type SubStepDefinition } from "@core/features/onboarding";
import { AvailabilitySubStep } from "./AvailabilitySubStep";
import { AddToCalendarSubStepContainer } from "../AddToCalendarSubStep";
import { SchedulingContainer } from "@platform/features/programScheduling";

export type Props = {
  /** The schedule action */
  action: Action | undefined;
  /** Sub-steps from the onboarding step definition */
  subSteps: SubStepDefinition[] | undefined;
  /** Program ID */
  programId: number;
  /** Callback when all sub-steps (or the single step) are completed */
  onComplete: () => void;
  /** Callback to skip this step */
  onSkip?: () => void;
};

/**
 * Schedule step content with optional sub-step wizard.
 *
 * If sub_steps are defined, renders a multi-screen wizard:
 * 1. Weekly availability selection
 * 2. Add to calendar
 *
 * If no sub_steps, renders the full SchedulingContainer.
 */
export function ScheduleStepContent({
  action,
  subSteps,
  programId,
  onComplete,
  onSkip,
}: Props) {
  const {
    hasSubSteps,
    currentSubStep,
    currentSubStepIndex,
    totalSubSteps,
    isFirstSubStep,
    isLastSubStep,
    nextSubStep,
    previousSubStep,
  } = useSubSteps({
    subSteps,
    onComplete,
  });

  // Extract scheduling config from action's display_options
  const displayOptions = action?.display_options as ScheduleActionDisplayOptions | null;

  // No sub-steps: render full scheduling container
  if (!hasSubSteps) {
    return (
      <SchedulingContainer
        programId={programId}
        scheduledEngagementStartDate={displayOptions?.scheduled_engagement_start_date}
        mode={displayOptions?.mode}
        selectableOptions={displayOptions?.selectableOptions}
        requiredSelections={displayOptions?.requiredSelections}
        maxSelections={displayOptions?.maxSelections}
        headerFormat={displayOptions?.headerFormat}
        onSuccess={onComplete}
        onSkip={onSkip}
        variant="inline"
        primaryButtonText="Save & Continue"
        label="Time slot"
      />
    );
  }

  // Find the calendar_add action if the current sub-step has one
  const calendarAction = currentSubStep?.action;

  // Render current sub-step
  const subStepProps = {
    programId,
    action,
    displayOptions,
    onNext: nextSubStep,
    onBack: isFirstSubStep ? undefined : previousSubStep,
    onSkip: isLastSubStep ? onSkip : undefined,
    stepIndex: currentSubStepIndex,
    totalSteps: totalSubSteps,
  };

  switch (currentSubStep?.slug) {
    case "availability":
      return <AvailabilitySubStep {...subStepProps} />;
    case "calendar":
      return (
        <AddToCalendarSubStepContainer
          {...subStepProps}
          calendarAction={calendarAction}
        />
      );
    default:
      // Unknown sub-step slug - fall back to full scheduling container
      return (
        <SchedulingContainer
          programId={programId}
          scheduledEngagementStartDate={displayOptions?.scheduled_engagement_start_date}
          mode={displayOptions?.mode}
          selectableOptions={displayOptions?.selectableOptions}
          requiredSelections={displayOptions?.requiredSelections}
          maxSelections={displayOptions?.maxSelections}
          headerFormat={displayOptions?.headerFormat}
          onSuccess={onComplete}
          onSkip={onSkip}
          variant="inline"
          primaryButtonText="Save & Continue"
          label="Time slot"
        />
      );
  }
}

export default ScheduleStepContent;
